import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card, Spinner, EmptyState } from "@/components/ui";
import { getShadow } from "@/components/ui/ShadowBadge";
import Button from "@/components/ui/Button";
import { useAuthStore } from "@/store/authStore";
import toast from "react-hot-toast";
import clsx from "clsx";

const PRIZES = [
  { ranks: "1",      icon: "🏆", label: "2 Wochen Schulfrei + Mega-Box + 5.000 Coins",   color: "#fbbf24" },
  { ranks: "2",      icon: "🥈", label: "1 Woche Schulfrei + Premium-Box + 3.000 Coins", color: "#9ca3af" },
  { ranks: "3",      icon: "🥉", label: "3 Tage Schulfrei + Deluxe-Schulset + 2.000",    color: "#b45309" },
  { ranks: "4–10",   icon: "✨", label: "1.000–500 Bonus-Coins + Shop-Voucher",           color: "#8b5cf6" },
  { ranks: "11–50",  icon: "⭐", label: "500–100 Bonus-Coins",                            color: "#06b6d4" },
  { ranks: "51–100", icon: "🎯", label: "100–10 Bonus-Coins",                             color: "#34d399" },
];

/* ─── Podium slot ────────────────────────────────────────────────────────────── */
function PodiumSlot({ entry, rank, height, isMe }) {
  const podiumColors = {
    1: { border: "#fbbf24", bg: "rgba(251,191,36,0.08)", glow: "rgba(251,191,36,0.4)", crown: "#fbbf24", label: "#fbbf24" },
    2: { border: "#9ca3af", bg: "rgba(156,163,175,0.06)", glow: "rgba(156,163,175,0.25)", crown: "#9ca3af", label: "#9ca3af" },
    3: { border: "#b45309", bg: "rgba(180,83,9,0.08)", glow: "rgba(180,83,9,0.3)", crown: "#b45309", label: "#cd7f32" },
  };
  const c = podiumColors[rank];

  return (
    <div className="flex-1 flex flex-col items-center">
      {/* Avatar + name */}
      <div className="text-center mb-2 relative">
        {/* Crown for 1st */}
        {rank === 1 && (
          <div className="text-2xl mb-1 animate-float" style={{ filter: "drop-shadow(0 0 8px rgba(251,191,36,0.8))" }}>👑</div>
        )}
        <div
          className={clsx("text-3xl mb-1", rank === 1 && "scale-110")}
          style={rank === 1 ? { animation: "float 3s ease-in-out infinite", filter: "drop-shadow(0 0 10px rgba(251,191,36,0.5))" } : undefined}
        >
          {entry?.avatar}
        </div>
        <p className="text-[10px] font-black truncate max-w-[70px]" style={{ color: c.label }}>
          {entry?.name?.split(" ")[0] || "–"}
        </p>
        <p className="text-[9px] text-gray-600">{entry?.xp?.toLocaleString("de-DE")} XP</p>
      </div>

      {/* Podium block */}
      <div
        className="w-full rounded-t-2xl flex items-start justify-center pt-3 relative overflow-hidden transition-all duration-500"
        style={{
          height,
          border:      `1px solid ${c.border}`,
          borderBottom: "none",
          background:  c.bg,
          boxShadow:   `0 -4px 20px ${c.glow}, inset 0 1px 0 ${c.border}40`,
        }}
      >
        {/* Shimmer */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `linear-gradient(180deg, ${c.border}12 0%, transparent 40%)` }}
        />
        <span className="text-xl font-black relative z-10" style={{ color: c.label }}>
          {rank === 1 ? "🏆" : rank === 2 ? "🥈" : "🥉"}
        </span>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuthStore();
  const [type,       setType]       = useState("halfyear");
  const [showPrizes, setShowPrizes] = useState(false);
  const qc                          = useQueryClient();
  const isStudent                   = user?.role === "student";

  const { data, isLoading } = useQuery({
    queryKey:       ["leaderboard", type],
    queryFn:        () => api.get(`/leaderboard?type=${type}`).then((r) => r.data),
    refetchInterval: 60_000,
  });

  const optIn = useMutation({
    mutationFn: (optInVal) => api.post("/leaderboard/optin", { optIn: optInVal }),
    onSuccess: (res) => {
      toast.success(res.data.message);
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
      qc.invalidateQueries({ queryKey: ["student", "me"] });
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const ranked  = data?.ranked || [];
  const season  = data?.season;
  const myRank  = data?.myRank;
  const myEntry = ranked.find((r) => r.userId === user?.id);
  const optedIn = !!myEntry;
  const xp      = user?.studentProfile?.xp || 0;
  const canOptIn = xp >= 400;

  if (isLoading) return (
    <div className="flex justify-center pt-20">
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 rounded-full border-2 border-t-yellow-500 border-r-violet-500 border-b-transparent border-l-transparent animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="text-center py-3 relative">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(251,191,36,0.06) 0%, transparent 70%)" }} />
        <div
          className="text-5xl mb-3 inline-block animate-float"
          style={{ filter: "drop-shadow(0 0 20px rgba(251,191,36,0.7)) drop-shadow(0 0 40px rgba(251,191,36,0.3))" }}
        >
          🏆
        </div>
        <h1 className="text-2xl font-black text-gradient-gold">Rangliste</h1>
        {season && <p className="text-xs text-gray-500 mt-1 font-medium">{season.name}</p>}
      </div>

      {/* ── Season type toggle ─────────────────────────────────────────────── */}
      <div className="flex gap-2 p-1 bg-nexus-s2 rounded-2xl border border-nexus-border">
        {[{ v: "halfyear", l: "Halbjährlich" }, { v: "year", l: "Jährlich" }].map((t) => (
          <button
            key={t.v}
            onClick={() => setType(t.v)}
            className={clsx(
              "flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-250",
              type === t.v
                ? "bg-gradient-to-r from-violet-600 to-cyan-600 text-white shadow-flow"
                : "text-gray-500 hover:text-gray-300"
            )}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* ── Prizes accordion ───────────────────────────────────────────────── */}
      <button
        onClick={() => setShowPrizes(!showPrizes)}
        className="w-full text-left p-4 rounded-2xl border border-yellow-500/25 hover:border-yellow-500/45 transition-all relative overflow-hidden group"
        style={{ background: "rgba(251,191,36,0.04)" }}
      >
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "radial-gradient(ellipse at center, rgba(251,191,36,0.05) 0%, transparent 70%)" }} />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span style={{ filter: "drop-shadow(0 0 6px rgba(251,191,36,0.6))" }}>🎁</span>
            <span className="font-black text-sm text-yellow-400">Saisonpreise</span>
          </div>
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#fbbf24" strokeLinecap="round" strokeLinejoin="round"
            className={clsx("transition-transform duration-300", showPrizes ? "rotate-180" : "")}
          ><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        {showPrizes && (
          <div className="mt-4 space-y-2.5 animate-fade-in">
            {PRIZES.map((p) => (
              <div key={p.ranks} className="flex items-center gap-2.5 text-xs">
                <span className="text-base flex-shrink-0" style={{ filter: `drop-shadow(0 0 4px ${p.color})` }}>{p.icon}</span>
                <span className="font-black flex-shrink-0" style={{ color: p.color }}>Platz {p.ranks}:</span>
                <span className="text-gray-400">{p.label}</span>
              </div>
            ))}
          </div>
        )}
      </button>

      {/* ── My rank card ───────────────────────────────────────────────────── */}
      {isStudent && (
        <Card className={clsx("p-4", optedIn ? "border-violet-500/45" : "border-dashed border-nexus-border")}>
          {optedIn ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-1">Dein Rang</p>
                <p
                  className="text-3xl font-black"
                  style={{ background: "linear-gradient(135deg, #8b5cf6, #06b6d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}
                >
                  #{myRank || "?"}
                </p>
                <p className="text-xs text-green-400 mt-0.5 font-medium">Du bist dabei! 🏆</p>
              </div>
              <Button variant="danger" size="sm" onClick={() => optIn.mutate(false)} loading={optIn.isPending}>
                Austragen
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <p className="font-black text-sm mb-1">🏆 An der Rangliste teilnehmen</p>
              <p className="text-xs text-gray-500 mb-3">
                {canOptIn
                  ? "Du hast ⚡ Blitzdenker-Rang und kannst mitmachen!"
                  : `Du brauchst 400 XP. Du hast ${xp} XP.`}
              </p>
              <Button onClick={() => optIn.mutate(true)} loading={optIn.isPending} disabled={!canOptIn} size="sm">
                {canOptIn ? "🚀 Jetzt einschreiben!" : `Noch ${400 - xp} XP nötig`}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* ── Top 3 Podium ───────────────────────────────────────────────────── */}
      {ranked.length >= 3 && (
        <div
          className="rounded-3xl p-4 pb-0 relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg, rgba(19,19,58,0.9) 0%, rgba(13,13,43,0.6) 100%)",
            border: "1px solid rgba(251,191,36,0.12)",
          }}
        >
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(251,191,36,0.06) 0%, transparent 60%)" }} />
          <div className="relative flex items-end justify-center gap-2 pt-2">
            <PodiumSlot entry={ranked[1]} rank={2} height={80}  isMe={ranked[1]?.userId === user?.id} />
            <PodiumSlot entry={ranked[0]} rank={1} height={110} isMe={ranked[0]?.userId === user?.id} />
            <PodiumSlot entry={ranked[2]} rank={3} height={65}  isMe={ranked[2]?.userId === user?.id} />
          </div>
        </div>
      )}

      {/* ── Full ranked list ───────────────────────────────────────────────── */}
      {ranked.length === 0 ? (
        <EmptyState icon="🏆" title="Noch keine Einträge" desc="Sei der/die Erste!" />
      ) : (
        <div className="space-y-1.5">
          {ranked.slice(3).map((entry, i) => {
            const rank   = i + 4;
            const isMe   = entry.userId === user?.id;
            const shadow = getShadow(entry.xp);
            return (
              <div
                key={entry.id}
                className={clsx(
                  "flex items-center gap-3 p-3.5 rounded-2xl border transition-all duration-200",
                  isMe
                    ? "border-violet-500/45 bg-violet-500/06"
                    : "border-nexus-border bg-nexus-sf hover:border-violet-500/20"
                )}
              >
                {/* Rank number */}
                <div
                  className={clsx(
                    "w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 font-black text-xs",
                    rank <= 10
                      ? "bg-violet-500/15 text-violet-400 border border-violet-500/30"
                      : "bg-nexus-s2 text-gray-500 border border-nexus-border"
                  )}
                >
                  {rank}
                </div>

                {/* Avatar */}
                <span className="text-xl flex-shrink-0">{entry.avatar}</span>

                {/* Name + rank badge */}
                <div className="flex-1 min-w-0">
                  <p className={clsx("font-bold text-sm truncate", isMe && "text-violet-400")}>
                    {entry.name} {isMe && <span className="text-violet-500">(Du)</span>}
                  </p>
                  <span className="text-[10px] text-gray-600">{shadow.emoji} {shadow.name}</span>
                </div>

                {/* XP */}
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-black text-violet-400">{entry.xp.toLocaleString("de-DE")}</p>
                  <p className="text-[10px] text-gray-600">XP</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="pb-2" />
    </div>
  );
}
